import { FC, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { useObservableState } from 'observable-hooks';
import { distinctUntilChanged } from 'rxjs';
import { map } from 'rxjs/operators';

import { BookingStateEnum, PassengerBookingView } from '@mopla/data-models';
import {
	calculateTotalPastCancellationPrice,
	checkBookingPriceReduced,
	checkIsBookingCancelled,
	checkTimeMightChange,
	getItineraryTimingV2,
	isLBTItinerary,
	useBreakpoints,
} from '@mopla/utils';

import {
	BookedTicketPassengersSummary,
	CancelButton,
	CheckmarkCircleIcon,
	IBookedTicketPassengersSummaryProps,
	IconMessage,
	InfoBaseIcon,
	InformationIcon,
	MplButton,
	RouteDetailsHeader,
} from '../../../';

import { useMoreActionsContext } from './MoreActions/context';
import { RouteDetails } from './ui/RouteDetails';
import { FailedStatusBlock } from './FailedStatusBlock';
import { PassengerInfoBlock } from './PassengerInfoBlock';

export interface IBookedRouteDetailsProps {
	cancelBooking?(): void;
	bookedItinerary: PassengerBookingView;
	invoiceContent?: ReactNode;
}

export const BookedRouteDetails: FC<IBookedRouteDetailsProps> = ({
	cancelBooking,
	bookedItinerary,
	invoiceContent,
}) => {
	const theme = useTheme();
	const { t } = useTranslation([
		'searchResults',
		'routeDetails',
		'tickets',
		'booking',
	]);
	const { itinerary } = bookedItinerary;
	const { isMobile } = useBreakpoints();
	const timeMightChange = checkTimeMightChange(itinerary);
	const isLBT = isLBTItinerary(itinerary);
	const isPriceReduction = checkBookingPriceReduced(bookedItinerary);
	const isExecuting =
		bookedItinerary.bookingStatus === BookingStateEnum.EXECUTING;
	const isFinished =
		bookedItinerary.bookingStatus === BookingStateEnum.FINISHED;
	const isCancelled = checkIsBookingCancelled(bookedItinerary);
	const cancellationFee = calculateTotalPastCancellationPrice(itinerary);

	const itineraryTimings = useMemo(
		() => getItineraryTimingV2(itinerary.legs),
		[itinerary]
	);

	const { openMoreActions } = useMoreActionsContext();

	const isFreeCancellation = isCancelled && cancellationFee === 0;

	const renderFn: IBookedTicketPassengersSummaryProps['render'] = ({
		mainPassengerName,
		attendees,
		luggage,
	}) => (
		<>
			<Typography variant="body2">{mainPassengerName}</Typography>
			{attendees && <Typography variant="body2">{attendees}</Typography>}
			{luggage && <Typography variant="body2">{luggage}</Typography>}
		</>
	);

	const [minutesBeforeStart] = useObservableState(() =>
		itineraryTimings.minutesBeforeStart$.pipe(
			map((value) => Math.ceil(value)),
			distinctUntilChanged()
		)
	);

	const delayWarning = useMemo(() => {
		if (
			isFinished ||
			!itineraryTimings.isDelayed ||
			minutesBeforeStart == null
		) {
			return null;
		}

		const departureTimeDiff = dayjs(
			itineraryTimings.effectiveStartDateObj
		).diff(itineraryTimings.startDateObj, 'minute');

		const arrivalTimeDiff = dayjs(itineraryTimings.effectiveEndDateObj).diff(
			itineraryTimings.endDateObj,
			'minute'
		);

		let message;

		if (minutesBeforeStart <= 30) {
			if (
				itineraryTimings.isStartTimeChanged &&
				itineraryTimings.isEndTimeChanged
			) {
				message = t('booking:traffic_consideration.pickup_and_arrival_changed');
			} else if (itineraryTimings.isStartTimeChanged) {
				message = t('booking:traffic_consideration.driver_delayed', {
					minutes: departureTimeDiff,
				});
			} else if (itineraryTimings.isEndTimeChanged) {
				message = t('booking:traffic_consideration.arrival_delayed', {
					minutes: arrivalTimeDiff,
				});
			}
		}

		if (message) {
			return (
				<IconMessage type="warning" icon={<InfoBaseIcon />} message={message} />
			);
		}

		return null;
	}, [isFinished, itineraryTimings, minutesBeforeStart, t]);

	const getFixedTimeMessage = () => {
		/** We usually want to show the "Time fixed" message for an itinerary if it is scheduled, in the future and not delayed */
		if (
			timeMightChange ||
			itineraryTimings.isDelayed ||
			isExecuting ||
			isFinished
		) {
			return null;
		}

		return (
			<IconMessage
				icon={<CheckmarkCircleIcon color={theme.palette.secondary.dark} />}
				message={t('common:text.time_fixed')}
				color="neutral.80"
				variant="body2"
			/>
		);
	};

	return (
		<RouteDetails
			route={itinerary}
			booking={bookedItinerary}
			cancellationFee={cancellationFee}
			routeDetailsHeaderBlock={
				<RouteDetailsHeader
					route={itinerary}
					bookedItinerary={bookedItinerary}
					messages={
						<>
							{delayWarning}
							{getFixedTimeMessage()}
							{isExecuting && (
								<IconMessage
									icon={
										<CheckmarkCircleIcon color={theme.palette.secondary.dark} />
									}
									message={t('common:text.checked_in_done')}
									color="neutral.80"
									variant="body2"
								/>
							)}
							{isPriceReduction && !isExecuting && !isFinished && (
								<IconMessage
									icon={<InformationIcon />}
									message={t('common:text.ticket_cheaper')}
									color="neutral.80"
									variant="body2"
								/>
							)}
						</>
					}
					isCancelled={isCancelled}
				/>
			}
			failedStatusBlock={
				<FailedStatusBlock
					isFreeCancellation={isFreeCancellation}
					bookedItinerary={bookedItinerary}
				/>
			}
			passengerInfoBlock={
				!isLBT &&
				isMobile && <PassengerInfoBlock bookedItinerary={bookedItinerary} />
			}
			passengerSummaryBlock={
				isMobile && (
					<BookedTicketPassengersSummary
						booking={bookedItinerary}
						render={renderFn}
					/>
				)
			}
			cancelButtonBlock={
				cancelBooking &&
				(isMobile ? (
					<MplButton
						fullWidth
						variant="outlined"
						onClick={() => openMoreActions(bookedItinerary)}
					>
						{t('common:text.more_actions')}
					</MplButton>
				) : (
					<CancelButton
						bookedItinerary={bookedItinerary}
						onClick={cancelBooking}
					/>
				))
			}
			isTicket
			isCancelled={isCancelled}
			invoiceContent={invoiceContent}
		/>
	);
};
